import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Send, Loader2, Tag, Trash2, GripVertical, Video } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth0Safe as useAuth0 } from '@/hooks/useAuth0Safe';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'nekretnine', label: 'Nekretnine' },
  { value: 'vozila', label: 'Vozila' },
  { value: 'elektronika', label: 'Elektronika' },
  { value: 'namjestaj', label: 'Namještaj / dom' },
  { value: 'odjeca', label: 'Odjeća / obuća' },
  { value: 'sport', label: 'Sport / hobi' },
  { value: 'usluge', label: 'Usluge' },
  { value: 'poljoprivreda', label: 'Poljoprivreda' },
  { value: 'djecji-svijet', label: 'Dječji svijet' },
  { value: 'ostalo', label: 'Ostalo' },
];

const MAX_IMAGES = 10;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;   // 10MB per image
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;  // 100MB total video

export default function ClassifiedSubmitPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [form, setForm] = useState({
    title: '', description: '', category: 'ostalo', price_eur: '',
    location: '', contact_name: '', contact_phone: '', contact_email: '',
    youtube_url: '',
  });
  const [images, setImages] = useState([]); // { data_url, name }
  const [videoData, setVideoData] = useState(null); // { data_url, name, size }
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  function onPickImages(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const max = MAX_IMAGES - images.length;
    files.slice(0, max).forEach(f => {
      if (f.size > MAX_IMAGE_BYTES) { toast.error(`${f.name}: slika prevelika (max 10MB)`); return; }
      const reader = new FileReader();
      reader.onload = () => setImages(prev => [...prev, { data_url: String(reader.result || ''), name: f.name }]);
      reader.readAsDataURL(f);
    });
    if (files.length > max) toast.error(`Maksimalno ${MAX_IMAGES} slika — dodano ih je ${max}`);
    e.target.value = '';
  }

  function removeImage(idx) { setImages(prev => prev.filter((_, i) => i !== idx)); }

  function onPickVideo(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_VIDEO_BYTES) { toast.error('Video prevelik (max 100MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => setVideoData({ data_url: String(reader.result || ''), name: f.name, size: f.size });
    reader.readAsDataURL(f);
    e.target.value = '';
  }

  // Drag & drop reorder for the image gallery.
  function onDragStart(idx) { setDragIdx(idx); }
  function onDragOver(e, idx) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setImages(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(idx);
  }
  function onDragEnd() { setDragIdx(null); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Morate biti prijavljeni za predaju oglasa');
      loginWithRedirect();
      return;
    }
    if (!form.title.trim() || !form.description.trim() || !form.contact_email.trim()) {
      toast.error('Molimo ispunite naslov, opis i kontakt e-mail');
      return;
    }
    setSending(true);
    try {
      const token = await getAccessTokenSilently();
      const payload = {
        title: form.title.trim(),
        description: form.description,
        category: form.category,
        price_eur: form.price_eur ? Number(form.price_eur) : null,
        location: form.location.trim(),
        // First image is the "featured" image in the listing.
        images: images.map(i => i.data_url),
        video_url: videoData?.data_url || null,
        youtube_url: form.youtube_url.trim() || null,
        contact_name: form.contact_name.trim(),
        contact_phone: form.contact_phone.trim(),
        contact_email: form.contact_email.trim(),
      };
      const res = await fetch(`${API}/public/classifieds/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSent(true);
      toast.success('Oglas uspješno predan!');
    } catch (err) { toast.error('Greška pri slanju: ' + err.message); }
    finally { setSending(false); }
  }

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="classified-submit-success">
        <div className="text-5xl mb-4">✅</div>
        <h1 className="text-2xl font-bold mb-3 text-gray-900 dark:text-gray-100">Hvala!</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-4">Vaš oglas je zaprimljen i čeka kratki pregled urednika prije objave.</p>
        <div className="flex gap-3 justify-center">
          <Link to="/oglasnik" className="inline-block px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg font-medium">Natrag na Oglasnik</Link>
          <button onClick={() => navigate('/moji-oglasi')} className="inline-block px-5 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-sm rounded-lg font-medium">Moji oglasi</button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="classified-login-required">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-red-600/10 text-red-600 rounded-full mb-3"><Tag size={26} /></div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Prijava potrebna</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-5">Za predaju oglasa morate biti prijavljeni. Tako oglas možete kasnije urediti ili obrisati iz vaše korisničke kontrole.</p>
        <button onClick={() => loginWithRedirect()} className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg font-medium" data-testid="classified-login-btn">Prijavi se</button>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Predaj oglas — e-mina.eu</title></Helmet>
      <div className="max-w-2xl mx-auto px-4 py-10" data-testid="classified-submit-page">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-red-600/10 text-red-600 rounded-full mb-3"><Tag size={26} /></div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Predaj oglas u Oglasnik</h1>
          <p className="text-gray-500 dark:text-gray-400">Prodajete, iznajmljujete, darujete? Objavite oglas besplatno na e-mina.eu.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 bg-white dark:bg-[#111827] rounded-xl border border-gray-200 dark:border-gray-800 p-6">
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block md:col-span-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Naslov oglasa *</span>
              <input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-red-400"
                placeholder="npr. Škoda Fabia 1.2, 2018. god." data-testid="classified-title" required />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kategorija</span>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:bg-[#0f172a] dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none" data-testid="classified-category">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cijena (EUR)</span>
              <input type="number" step="0.01" min="0" value={form.price_eur} onChange={e => setForm(p => ({ ...p, price_eur: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none"
                placeholder="npr. 5 000" data-testid="classified-price" />
            </label>
            <label className="block md:col-span-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Opis *</span>
              <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                rows={6} className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-red-400 resize-y"
                placeholder="Detaljan opis: stanje, godina, kilometraža, razlog prodaje…" data-testid="classified-description" required />
            </label>

            {/* Images — up to 10, 10MB each, drag-and-drop reorder */}
            <div className="block md:col-span-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Slike (do {MAX_IMAGES}, max 10MB svaka). Prva slika se koristi kao istaknuta na popisu.
              </span>
              <input type="file" accept="image/*" multiple onChange={onPickImages} className="text-sm dark:text-gray-300" data-testid="classified-images" />
              {images.length > 0 && (
                <div className="mt-3 grid grid-cols-4 sm:grid-cols-5 gap-2" data-testid="classified-image-gallery">
                  {images.map((im, i) => (
                    <div key={i}
                         draggable
                         onDragStart={() => onDragStart(i)}
                         onDragOver={(e) => onDragOver(e, i)}
                         onDragEnd={onDragEnd}
                         className={`relative group border-2 rounded ${i === 0 ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'} ${dragIdx === i ? 'opacity-60' : ''}`}
                         data-testid={`classified-image-${i}`}>
                      <img src={im.data_url} alt="" className="h-20 w-full object-cover rounded" />
                      {i === 0 && <span className="absolute top-1 left-1 text-[9px] bg-red-600 text-white px-1.5 py-0.5 rounded">ISTAKNUTA</span>}
                      <button type="button" onClick={() => removeImage(i)}
                        className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full p-0.5"
                        data-testid={`classified-image-remove-${i}`}>
                        <Trash2 size={10} />
                      </button>
                      <span className="absolute bottom-1 right-1 bg-black/60 text-white p-1 rounded cursor-grab" title="Povuci za premještanje">
                        <GripVertical size={10} />
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">Povucite sliku za promjenu redoslijeda — prva slika je istaknuta na popisu oglasa.</p>
            </div>

            {/* Video upload — up to 100MB */}
            <label className="block md:col-span-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Video (max 100MB) — ili koristite YouTube link niže</span>
              <input type="file" accept="video/*" onChange={onPickVideo} className="text-sm dark:text-gray-300" data-testid="classified-video" />
              {videoData && (
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <Video size={14} /> {videoData.name} · {(videoData.size / 1024 / 1024).toFixed(1)}MB
                  <button type="button" onClick={() => setVideoData(null)} className="text-red-500" data-testid="classified-video-remove">Ukloni</button>
                </div>
              )}
            </label>

            {/* YouTube alternative */}
            <label className="block md:col-span-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">YouTube link (alternativa videu)</span>
              <input type="url" value={form.youtube_url} onChange={e => setForm(p => ({ ...p, youtube_url: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none"
                placeholder="https://www.youtube.com/watch?v=..." data-testid="classified-youtube" />
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lokacija</span>
              <input type="text" value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none"
                placeholder="Grad / županija" data-testid="classified-location" />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ime i prezime</span>
              <input type="text" value={form.contact_name} onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none"
                placeholder="Vaše ime" data-testid="classified-contact-name" />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefon</span>
              <input type="tel" value={form.contact_phone} onChange={e => setForm(p => ({ ...p, contact_phone: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none"
                placeholder="+385 …" data-testid="classified-contact-phone" />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">E-mail *</span>
              <input type="email" value={form.contact_email} onChange={e => setForm(p => ({ ...p, contact_email: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-red-400"
                placeholder="vas@email.com" data-testid="classified-contact-email" required />
            </label>
          </div>
          <button type="submit" disabled={sending}
            className="w-full flex items-center justify-center gap-2 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            data-testid="classified-submit-btn">
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            {sending ? 'Šaljem…' : 'Predaj oglas'}
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center" data-testid="classified-rules-note">
            Urednik zadržava pravo uklanjanja ili pauziranja oglasa ukoliko nije s pravilima i standardima portala.
          </p>
        </form>
      </div>
    </>
  );
}
